// Updated: 2026-08-20T15:15:00Z
// Purpose: Convert persisted screenplay sequences into editable blocks, preserve text selections, and rebuild generation-safe character data.

import type {
  Character,
  SequenceItem,
  TedIntonationSpec,
} from "@/types/screenplay";
import { normalizeInlinePauseNotation, replaceEditorText } from './inlinePauses';

export type ScreenplayEditorBlockType =
  | "scene-heading"
  | "action"
  | "dialogue"
  | "transition";

export interface ScreenplayEditorBlock {
  id: string;
  type: ScreenplayEditorBlockType;
  text: string;
  character?: string;
  parenthetical?: string;
  parentheticalId?: string;
  intonation?: TedIntonationSpec;
  speechLanguage?: SequenceItem['speechLanguage'];
}

export interface ScreenplayTextHighlight {
  blockId: string;
  text: string;
  start: number;
  end: number;
}

export const highlightAfterBlockSelection = (
  highlight: ScreenplayTextHighlight | null,
  selectedBlockId: string,
): ScreenplayTextHighlight | null =>
  highlight?.blockId === selectedBlockId ? highlight : null;

const normalizeCharacterName = (value: string) =>
  value.replace(/\s+/g, " ").trim().toUpperCase();

const normalizedEditorType = (
  type: SequenceItem["type"],
): ScreenplayEditorBlockType => {
  if (type === "scene-heading") return "scene-heading";
  if (type === "dialogue") return "dialogue";
  if (type === "transition") return "transition";
  return "action";
};

export const screenplaySequence = (characters: Character[]): SequenceItem[] =>
  characters.find((character) => character.name === "SCREENPLAY_SEQUENCE")
    ?.sequence ?? [];

export const sequenceToEditorBlocks = (
  sequence: SequenceItem[],
): ScreenplayEditorBlock[] => {
  const ordered = [...sequence].sort((left, right) => left.order - right.order);
  const blocks: ScreenplayEditorBlock[] = [];

  ordered.forEach((item, index) => {
    if (item.type === "parenthetical") {
      const next = ordered[index + 1];
      if (next?.type === "dialogue") return;

      blocks.push({
        id: item.id?.trim() || `editor-${index}-${item.order}`,
        type: "action",
        text: item.text,
      });
      return;
    }

    const previous = ordered[index - 1];
    const parenthetical =
      item.type === "dialogue" && previous?.type === "parenthetical"
        ? previous
        : undefined;

    blocks.push({
      id: item.id?.trim() || `editor-${index}-${item.order}`,
      type: normalizedEditorType(item.type),
      text: item.text,
      character:
        item.type === "dialogue"
          ? normalizeCharacterName(item.character ?? "")
          : undefined,
      parenthetical: parenthetical?.text,
      parentheticalId: parenthetical?.id?.trim(),
      intonation: item.intonation,
      ...(item.speechLanguage !== undefined ? { speechLanguage: item.speechLanguage } : {}),
    });
  });

  return blocks.map(block => replaceEditorText(normalizeInlinePauseNotation(block),0,0,'').block);
};

export const editorBlocksToSequence = (
  blocks: ScreenplayEditorBlock[],
): SequenceItem[] =>
  blocks
    .filter((block) => block.text.trim().length > 0)
    .flatMap((block) => {
      const text = block.text.trim();
      const leadingCharacters = block.text.length - block.text.trimStart().length;
      const intonation = block.intonation
        ? {
            ...block.intonation,
            spans: block.intonation.spans
              .map((span) => ({
                ...span,
                start: Math.max(0, span.start - leadingCharacters),
                end: Math.min(text.length, span.end - leadingCharacters),
              }))
              .filter((span) => span.end > span.start),
          }
        : undefined;

      const dialogue: SequenceItem = {
        id: block.id,
        type: block.type,
        text,
        order: 0,
        character:
          block.type === "dialogue"
            ? normalizeCharacterName(block.character ?? "UNKNOWN CHARACTER")
            : undefined,
        intonation: intonation?.spans.length ? intonation : undefined,
        ...(block.speechLanguage !== undefined ? { speechLanguage: block.speechLanguage } : {}),
      };

      if (block.type !== "dialogue" || !block.parenthetical?.trim()) {
        return [dialogue];
      }

      return [
        {
          id: block.parentheticalId?.trim() || `${block.id}-parenthetical`,
          type: "parenthetical",
          character: normalizeCharacterName(block.character ?? "UNKNOWN CHARACTER"),
          text: block.parenthetical.trim(),
          order: 0,
        } satisfies SequenceItem,
        dialogue,
      ];
    })
    .map((item, order) => ({ ...item, order }));

export const rebuildCharactersFromSequence = (
  sequence: SequenceItem[],
  previousCharacters: Character[],
): Character[] => {
  const previousByName = new Map(
    previousCharacters.map((character) => [character.name, character]),
  );
  const dialogueByCharacter = new Map<string, string[]>();
  let narratedLines = 0;

  sequence.forEach((item) => {
    if (item.type === "dialogue") {
      const characterName = normalizeCharacterName(
        item.character ?? "UNKNOWN CHARACTER",
      );
      const dialogue = dialogueByCharacter.get(characterName) ?? [];
      dialogue.push(item.text);
      dialogueByCharacter.set(characterName, dialogue);
      return;
    }

    if (item.type !== "parenthetical") narratedLines += 1;
  });

  const characters: Character[] = [
    {
      name: "SCREENPLAY_SEQUENCE",
      lines: sequence.length,
      description: "Ordered screenplay sequence",
      sequence,
    },
  ];

  if (narratedLines > 0) {
    characters.push({
      name: "SCENE NARRATOR",
      lines: narratedLines,
      description:
        previousByName.get("SCENE NARRATOR")?.description ??
        "Scene headings, narration, action, and transitions",
      dialogue: sequence
        .filter((item) => item.type !== "dialogue" && item.type !== "parenthetical")
        .map((item) => item.text),
    });
  }

  dialogueByCharacter.forEach((dialogue, name) => {
    characters.push({
      name,
      lines: dialogue.length,
      description:
        previousByName.get(name)?.description ?? "Speaking character",
      dialogue,
    });
  });

  return characters;
};

export const recentCharacterNames = (
  blocks: ScreenplayEditorBlock[],
  limit = 8,
): string[] => {
  const names: string[] = [];

  [...blocks].reverse().forEach((block) => {
    const character = normalizeCharacterName(block.character ?? "");
    if (
      block.type === "dialogue" &&
      character &&
      !names.includes(character) &&
      names.length < limit
    ) {
      names.push(character);
    }
  });

  return names;
};

export const sceneBlockRange = (
  blocks: ScreenplayEditorBlock[],
  activeIndex: number,
): [number, number] | null => {
  if (activeIndex < 0 || activeIndex >= blocks.length) return null;

  let start = activeIndex;
  while (start > 0 && blocks[start].type !== "scene-heading") {
    start -= 1;
  }
  if (blocks[start].type !== "scene-heading") start = 0;

  let end = start + 1;
  while (end < blocks.length && blocks[end].type !== "scene-heading") {
    end += 1;
  }

  return [start, Math.max(start, end - 1)];
};

export const markerBlockRange = (
  blocks: ScreenplayEditorBlock[],
  startId: string | null,
  endId: string | null,
): [number, number] | null => {
  if (!startId || !endId) return null;

  const start = blocks.findIndex((block) => block.id === startId);
  const end = blocks.findIndex((block) => block.id === endId);

  if (start < 0 || end < 0) return null;
  return start <= end ? [start, end] : [end, start];
};
